/**
 * Invite Codes API — Single-use codes for controlled signup access
 */

import supabase from "../config/supabase";
import { invokeFunction } from "./functionErrors";

// Only used if the database is unreachable, so the screen can still lay out
// three slots instead of none. The real number comes from the database.
const DEFAULT_ALLOWANCE = 3;

// Anyone opening this link lands on sign-up with the code already filled in.
const buildInviteLink = (code) => {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://outbackexchange.ai";
  return `${origin}/sign-up?invite=${code}`;
};

const inviteCodesApi = {
  /**
   * Generate a new invite code
   * @param {string} intendedRecipient - Optional note about who this code is for
   * @returns {Promise<{success: boolean, data?: {code: string, link: string, qrData: string}, error?: string}>}
   */
  async generate(intendedRecipient = null) {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { success: false, error: "Not authenticated" };
      }

      // Generate the code
      const { data: codeData, error: generateError } = await supabase.rpc(
        "generate_invite_code"
      );

      if (generateError || !codeData) {
        return {
          success: false,
          error: generateError?.message || "Failed to generate code",
        };
      }

      const code = codeData;

      // Insert the code record
      const { error: insertError } = await supabase
        .from("invite_codes")
        .insert({
          code,
          intended_recipient: intendedRecipient || null,
          created_by: user.id,
        });

      if (insertError) {
        return { success: false, error: insertError.message };
      }

      // Build the shareable link and QR data
      const link = buildInviteLink(code);
      const qrData = link;

      return {
        success: true,
        data: { code, link, qrData },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to generate invite code",
      };
    }
  },

  /**
   * The signed-in member's own codes, with the first name of whoever used each.
   * Goes through a function because members have no read access to the table.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async listMine() {
    try {
      const { data, error } = await supabase.rpc("get_my_invite_codes");

      if (error) {
        return { success: false, error: error.message };
      }

      const codes = (data || []).map((row) => ({
        code: row.code,
        intendedFor: row.intended_recipient || null,
        isActive: row.is_active,
        claimedAt: row.claimed_at,
        claimedByName: row.claimed_by_name || null,
        createdAt: row.created_at,
        link: buildInviteLink(row.code),
      }));

      return { success: true, data: codes };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load your invite codes",
      };
    }
  },

  /**
   * Mint one of the member's own three codes.
   * `atLimit` comes back true when they've already handed out all three — that's
   * an expected answer, not a failure, so it isn't reported as an error.
   * @param {string|null} intendedRecipient - Optional note about who it's for
   * @returns {Promise<{success: boolean, data?: {code: string, link: string, atLimit: boolean}, error?: string}>}
   */
  async createMine(intendedRecipient = null) {
    try {
      const { data, error } = await supabase.rpc("create_my_invite_code", {
        p_intended_recipient: intendedRecipient || null,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data) {
        return { success: true, data: { atLimit: true } };
      }

      return {
        success: true,
        data: { code: data, link: buildInviteLink(data), atLimit: false },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to create an invite code",
      };
    }
  },

  /**
   * How many codes each member gets. Read from the database so changing the
   * allowance doesn't need an app release.
   * @returns {Promise<number>}
   */
  async allowance() {
    try {
      const { data, error } = await supabase.rpc("member_invite_allowance");
      if (error || typeof data !== "number") return DEFAULT_ALLOWANCE;
      return data;
    } catch (error) {
      return DEFAULT_ALLOWANCE;
    }
  },

  /**
   * Validate an invite code (check if it's usable)
   * @param {string} code - The 6-character code to validate
   * @returns {Promise<{success: boolean, data?: {valid: boolean, error?: string}, error?: string}>}
   */
  async validate(code) {
    try {
      if (!code || typeof code !== "string") {
        return {
          success: true,
          data: { valid: false, error: "Code is required" },
        };
      }

      const normalized = code.toUpperCase().trim();

      // Check format
      if (!/^[A-Z0-9]{6}$/.test(normalized)) {
        return {
          success: true,
          data: { valid: false, error: "Invalid code format" },
        };
      }

      // Goes through the signup function rather than the database directly, so
      // the check is metered per IP. Called straight from the browser it was an
      // unmetered way to test guessed codes.
      const result = await invokeFunction(
        supabase,
        "signup",
        { body: { action: "validate-code", code: normalized } },
        "Could not check that code. Please try again.",
      );

      if (!result.success) return result;

      if (result.data?.valid === true) {
        return { success: true, data: { valid: true } };
      }

      return {
        success: true,
        data: { valid: false, error: "Code is invalid or already used" },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to validate code",
      };
    }
  },

  /**
   * List all invite codes (admin only)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async list() {
    try {
      const { data, error } = await supabase
        .from("invite_codes")
        .select(
          `
          code,
          intended_recipient,
          claimed_by,
          claimed_at,
          created_at,
          is_active,
          claimer:claimed_by(first_name, last_name)
        `
        )
        .order("is_active", { ascending: false })
        .order("claimed_by", { ascending: true, nullsFirst: true })
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      // Transform data for display
      const codes = (data || []).map((code) => {
        let status = "Available";
        let claimedBy = "—";

        if (!code.is_active) {
          status = "Disabled";
        } else if (code.claimed_by) {
          status = "Claimed";
          if (code.claimer) {
            claimedBy = `${code.claimer.first_name} ${code.claimer.last_name}`;
          }
        }

        return {
          code: code.code,
          intendedFor: code.intended_recipient || "—",
          status,
          claimedBy,
          claimedAt: code.claimed_at,
          createdAt: code.created_at,
          isActive: code.is_active,
        };
      });

      return { success: true, data: codes };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load invite codes",
      };
    }
  },

  /**
   * Disable an invite code (admin only)
   * @param {string} code - The code to disable
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async disable(code) {
    try {
      const { error } = await supabase
        .from("invite_codes")
        .update({ is_active: false })
        .eq("code", code);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to disable code",
      };
    }
  },
};

export default inviteCodesApi;
